import { gql, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import billingQuery from '../admin/billing/billingQuery';
import Button from '../components/Button';
import Modal, { ModalTitle } from '../components/Modal';
import ModeToggle from '../components/ModeToggle';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import type {
  PricingModalBillingQuery,
  PricingModalBillingQueryVariables,
  PricingModalPlanTierQuery,
  PricingModalPlanTierQueryVariables,
} from '../graphql/generated/graphqlTypes';
import { PlanInterval, PlanTier } from '../graphql/generated/graphqlTypes';
import boltIcon from '../icons/bolt.svg';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import { useLocation } from '../utils/router/v3Bridge';
import type { PlanFeatureKey } from './PricingConfig';
import { PlanFeatureKeysOrdered, PlanFeatures } from './PricingConfig';
import styles from './PricingModal.module.scss';
import PricingModalMobileSection from './PricingModalMobileSection';
import PricingRow from './PricingModalRow';
import PricingModalTierLabel from './PricingModalTierLabel';
import UpgradeButton from './UpgradeButton';

const PLAN_TIER_QUERY = gql`
  query PricingModalPlanTier {
    session: currentSession {
      organization {
        id
        planTier
      }
    }
  }
`;

const BILLING_QUERY = gql`
  query PricingModalBilling {
    billingPricing {
      ...PricingModalTierLabel_billingPricing
    }
    billingSummary {
      plan {
        interval
      }
      trialEndAt
    }
  }

  ${PricingModalTierLabel.fragments.billingPricing}
`;

interface PricingModalProps {
  close: () => void;
  highlight?: PlanFeatureKey;
}

const PricingModal = ({ close, highlight }: PricingModalProps) => {
  const location = useLocation();

  const queryHighlight =
    typeof location.query.highlight === 'string'
      ? (location.query.highlight as PlanFeatureKey) ?? null
      : null;

  const activeRow = queryHighlight ?? highlight;

  // @ts-expect-error
  const user = useSelector((state) => state.session.user);
  const isAdmin = user.type === 'ADMIN';

  const { data: planTierData } = useQuery<
    PricingModalPlanTierQuery,
    PricingModalPlanTierQueryVariables
  >(PLAN_TIER_QUERY);

  const { data } = useQuery<
    PricingModalBillingQuery,
    PricingModalBillingQueryVariables
  >(BILLING_QUERY, {
    skip: !isAdmin,
  });

  const isMobile = useIsSize(Sizes.SM);

  const [interval, setInterval] = useState<PlanInterval>(
    data?.billingSummary?.plan?.interval ?? PlanInterval.Monthly,
  );

  const activeTier =
    planTierData?.session?.organization?.planTier ?? PlanTier.Free;

  const hasTried = data?.billingSummary?.trialEndAt != null;
  const upgradeCTA = hasTried ? 'Upgrade' : 'Try now';

  const isFree = activeTier === PlanTier.Free;
  const isStartup = activeTier === PlanTier.Startup;
  const isBusiness = activeTier === PlanTier.Business;
  const isEnterprise = activeTier === PlanTier.Enterprise;

  const contactSalesUrl = `https://slab.com/contact-sales/?email=${user.email}&name=${user.name}`;

  return (
    <Modal width={638}>
      {isMobile ? (
        <>
          <ModalTitle
            close={close}
            subtitle={
              <>
                Your team is currently on the{' '}
                <span className={styles.tierName}>
                  {activeTier.toLowerCase()}
                </span>{' '}
                plan
              </>
            }
          >
            Compare plans
          </ModalTitle>
          <div>
            <PricingModalMobileSection
              name="Free"
              active={isFree}
              tier={PlanTier.Free}
            />
            <PricingModalMobileSection
              name="Startup"
              active={isStartup}
              canTrial={!hasTried}
              canUpgrade={!isBusiness && !isEnterprise}
              to={`/admin/billing?${billingQuery(PlanTier.Startup, interval)}`}
              tier={PlanTier.Startup}
            />
            <PricingModalMobileSection
              name="Business"
              active={isBusiness}
              canTrial={!hasTried}
              to={`/admin/billing?${billingQuery(PlanTier.Business, interval)}`}
              tier={PlanTier.Business}
              canUpgrade={!isEnterprise}
            />
            {[PlanTier.Business, PlanTier.Enterprise].includes(activeTier) && (
              <PricingModalMobileSection
                name="Enterprise"
                active={isEnterprise}
                to={contactSalesUrl}
                tier={PlanTier.Enterprise}
                canUpgrade
                canTrial={false}
              />
            )}
          </div>
        </>
      ) : (
        <>
          <ModalTitle
            close={close}
            subtitle={
              <>
                Need help?{' '}
                <Button
                  variant="link"
                  onClick={() => {
                    window.Intercom?.('show');
                  }}
                >
                  Contact support
                </Button>{' '}
                with any questions or needs
              </>
            }
          >
            Compare plans
          </ModalTitle>
          <div className={styles.pricingTable}>
            <PricingRow
              label={
                isAdmin ? (
                  <ModeToggle
                    left="Monthly"
                    right="Annual"
                    leftActive={interval === PlanInterval.Monthly}
                    onToggle={() =>
                      setInterval((i) =>
                        i === PlanInterval.Monthly
                          ? PlanInterval.Yearly
                          : PlanInterval.Monthly,
                      )
                    }
                  />
                ) : undefined
              }
              free={
                <PricingModalTierLabel
                  tier={PlanTier.Free}
                  billingPricing={data?.billingPricing}
                  interval={interval}
                />
              }
              startup={
                <PricingModalTierLabel
                  tier={PlanTier.Startup}
                  billingPricing={data?.billingPricing}
                  interval={interval}
                />
              }
              business={
                <PricingModalTierLabel
                  tier={PlanTier.Business}
                  billingPricing={data?.billingPricing}
                  interval={interval}
                />
              }
              activeTier={activeTier}
              type="HEADER"
            />
            {PlanFeatureKeysOrdered.map((key) => {
              if (key !== activeRow && PlanFeatures[key].optional) return null;
              return (
                <PricingRow
                  key={key}
                  label={PlanFeatures[key].name}
                  free={PlanFeatures[key].FREE}
                  startup={PlanFeatures[key].STARTUP}
                  business={PlanFeatures[key].BUSINESS}
                  activeTier={activeTier}
                  activeRow={activeRow === key}
                />
              );
            })}
            <PricingRow
              free={isFree ? 'Current Plan' : undefined}
              startup={
                <>
                  {isStartup && 'Current Plan'}
                  {isFree && (
                    <Tooltip
                      tooltip={!isAdmin ? 'Ask an admin to upgrade' : undefined}
                      placement="top"
                      trigger="hover"
                    >
                      <UpgradeButton
                        disabled={!isAdmin}
                        onClick={close}
                        to={`/admin/billing?${billingQuery(
                          PlanTier.Startup,
                          interval,
                        )}`}
                      >
                        {upgradeCTA}
                      </UpgradeButton>
                    </Tooltip>
                  )}
                </>
              }
              business={
                <>
                  {isBusiness && 'Current Plan'}
                  {(isFree || isStartup) && (
                    <Tooltip
                      tooltip={!isAdmin ? 'Ask an admin to upgrade' : undefined}
                      placement="top"
                      trigger="hover"
                    >
                      <UpgradeButton
                        disabled={!isAdmin}
                        onClick={close}
                        to={`/admin/billing?${billingQuery(
                          PlanTier.Business,
                          interval,
                        )}`}
                      >
                        {upgradeCTA}
                      </UpgradeButton>
                    </Tooltip>
                  )}
                </>
              }
              activeTier={activeTier}
              type="FOOTER"
            />
          </div>
          {isBusiness && isAdmin && (
            <div className={styles.enterpriseCallout}>
              <div>
                <div className={styles.enterpriseCalloutHeader}>
                  Do more with Enterprise
                </div>
                <div>
                  The Enterprise plan offers complete access, customer success,
                  and custom limits based on your needs
                </div>
              </div>
              <Button
                className={styles.upgradeButton}
                disabled={!isAdmin}
                icon={
                  <SVGIcon className={styles.boltIcon} coloringWith="fill">
                    {boltIcon}
                  </SVGIcon>
                }
                variant="secondary"
                onClick={close}
                to={contactSalesUrl}
              >
                Contact&nbsp;sales
              </Button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default PricingModal;
